import { useEffect, useRef, useState } from "react";
import { Sheet } from "../../components/Sheet";
import { CheckIcon } from "../../components/icons";
import { Button, Spinner } from "../../components/ui";
import { cn } from "../../lib/cn";
import { useToast } from "../../components/Toast";
import { useCheckIn } from "./api";
import { requiredGoalFor } from "../goals/requiredGoals";
import { errorMessage } from "../../lib/errors";
import { cadenceLabel, periodLabel } from "../../lib/time";
import type { GoalWithStats } from "../goals/api";

/**
 * The check-in.
 *
 * The design routes every check-in through the sheet — note and photo are
 * optional, but the confirm tap is not — and answers with a success panel
 * rather than a toast, so the streak moving is the last thing you see.
 */
type Phase =
  { kind: "closed" } | { kind: "form" } | { kind: "logged"; line: string };

const CLOSE_DELAY_MS = 1500;

export function CheckInButton({
  goal,
  ownerId,
  block = false,
  className,
  label,
}: {
  goal: GoalWithStats;
  ownerId: string;
  /** Full-width, for the bottom of a goal. */
  block?: boolean;
  className?: string;
  /** Overrides the button text (the sheet is unaffected). */
  label?: string;
}) {
  const checkIn = useCheckIn(ownerId);
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const complete = goal.stats?.period_complete ?? false;
  const required = requiredGoalFor(goal);
  const photoRequired = required !== undefined || goal.photo_required;
  const noteRequired = required?.noteRequired ?? false;
  const missingProof =
    (photoRequired && !photo) || (noteRequired && !note.trim());

  function submit() {
    if (missingProof) return;
    checkIn.mutate(
      { goalId: goal.id, goalTitle: goal.title, note, photo },
      {
        onSuccess: ({ queued }) => {
          setNote("");
          setPhoto(null);
          setPhase({ kind: "logged", line: outcomeLine(goal, queued) });
          closeTimer.current = window.setTimeout(
            () => setPhase({ kind: "closed" }),
            CLOSE_DELAY_MS,
          );
        },
        onError: (error) =>
          toast(errorMessage(error, "Check-in failed"), "error"),
      },
    );
  }

  return (
    <>
      <Button
        onClick={() => setPhase({ kind: "form" })}
        aria-label={`Check in on ${goal.title}`}
        variant={complete ? "secondary" : "primary"}
        className={cn(
          block ? "btn-block" : "px-3.5 py-[7px] text-xs",
          className,
        )}
      >
        {label ?? (complete ? "check in again" : "check in")}
      </Button>

      <Sheet
        open={phase.kind !== "closed"}
        onClose={() => {
          window.clearTimeout(closeTimer.current);
          setPhase({ kind: "closed" });
        }}
        title={phase.kind === "logged" ? "logged" : goal.title}
        titleHidden={phase.kind === "logged"}
        subtitle={
          phase.kind === "logged"
            ? undefined
            : `${cadenceLabel(goal.cadence, goal.target_per_period)} · today's check-in`
        }
      >
        {phase.kind === "logged" ? (
          <div className="flex flex-col items-center gap-2.5 py-8 text-center">
            <span
              aria-hidden
              className="flex size-12 items-center justify-center rounded-full text-[oklch(0.35_0.06_160)]"
              style={{
                background: "linear-gradient(135deg, oklch(0.88 0.08 175), oklch(0.82 0.1 155))",
              }}
            >
              <CheckIcon size={22} />
            </span>
            <p className="font-heading text-xl">Logged</p>
            <p className="font-mono text-[13px] text-neutral-500">{phase.line}</p>
            <p className="mt-2 text-[11px] text-neutral-500">closing…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <label className="sr-only" htmlFor="checkin-note">
              Note
            </label>
            <textarea
              id="checkin-note"
              rows={2}
              maxLength={500}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              required={noteRequired}
              placeholder={
                required
                  ? required.prompt +
                    (required.noteRequired ? "" : " (optional)")
                  : "add a note (optional)"
              }
              className="input min-h-16 resize-none"
            />

            <PhotoSlot photo={photo} onChange={setPhoto} />

            <Button
              onClick={submit}
              disabled={checkIn.isPending || missingProof}
              className="btn-block"
            >
              {checkIn.isPending ? (
                <Spinner className="border-t-current" />
              ) : (
                "Check in →"
              )}
            </Button>

            <p className="text-center text-[11px] opacity-50">
              {missingProof
                ? required?.noteRequired
                  ? "a note and a photo are both required"
                  : "a photo is required"
                : "logs instantly · syncs after"}
            </p>
          </div>
        )}
      </Sheet>
    </>
  );
}

/**
 * What the success panel says. The streak moving is the interesting outcome;
 * when it has not moved, the period count is the next best thing.
 */
function outcomeLine(goal: GoalWithStats, queued: boolean): string {
  if (queued) return "saved here — syncs when you're back online";

  const stats = goal.stats;
  if (!stats) return "check-in recorded";

  const count = stats.period_count + 1;
  const nowComplete = count >= goal.target_per_period;

  if (nowComplete && !stats.period_complete) {
    return `streak ${stats.current_streak} → ${stats.current_streak + 1}`;
  }
  return `${count}/${goal.target_per_period} ${periodLabel(goal.cadence)}`;
}

/** The dashed square from the design — 72px, a plus, and the file name once set. */
function PhotoSlot({
  photo,
  onChange,
}: {
  photo: File | null;
  onChange: (file: File | null) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label
        className={cn(
          "flex size-[72px] shrink-0 cursor-pointer flex-col items-center justify-center rounded-2xl border-[1.5px] border-dashed text-center text-xs transition-colors",
          photo
            ? "border-accent-2-400 bg-accent-2-100 text-accent-2-800"
            : "border-neutral-400 bg-white text-neutral-500 hover:border-neutral-500",
        )}
      >
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          onChange={(event) => onChange(event.target.files?.[0] ?? null)}
        />
        <span aria-hidden>{photo ? <CheckIcon size={18} /> : "+ photo"}</span>
        <span className="sr-only">
          {photo ? "Photo attached" : "Add a photo"}
        </span>
      </label>

      {photo && (
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-xs opacity-60">
            {photo.name}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="shrink-0 text-xs text-neutral-600 hover:underline"
          >
            remove
          </button>
        </div>
      )}
    </div>
  );
}
